import { createSlice } from "@reduxjs/toolkit";
import {
  getOfferingsThunk,
  getOfferingByIdThunk,
  createOfferingThunk,
  reviseOfferingThunk,
  submitOfferingThunk,
  actApprovalThunk,
  sendOfferingThunk,
  acceptOfferingThunk,
  declineOfferingThunk,
  cancelOfferingThunk,
  convertOfferingThunk,
  saveOfferingThunk,
  setOfferingRevenueSchemeThunk,
} from "state/action/offering";

const async0 = () => ({
  data: null,
  isPending: false,
  isFulfilled: false,
  isRejected: false,
  err: null,
});

const pending = (s) => ({
  ...s,
  data: null,
  isPending: true,
  isFulfilled: false,
  isRejected: false,
  err: null,
});
const fulfilled = (s, a) => ({
  ...s,
  data: a.payload,
  isPending: false,
  isFulfilled: true,
  isRejected: false,
  err: null,
});
const rejected = (s, a) => ({
  ...s,
  data: null,
  isPending: false,
  isFulfilled: false,
  isRejected: true,
  err: a.error.message,
});

const initialState = {
  getOfferings: async0(),
  getOfferingById: async0(),
  createOffering: async0(),
  reviseOffering: async0(),
  submitOffering: async0(),
  actApproval: async0(),
  saveOffering: async0(),
  sendOffering: async0(),
  acceptOffering: async0(),
  declineOffering: async0(),
  cancelOffering: async0(),
  convertOffering: async0(),
  setRevenueScheme: async0(),

  activeStep: 0,
  letterOpening: "",
  letterClosing1: "",
  letterClosing2: "",
  expandedCosts: {},
  info: {
    title: "",
    offeringNumber: "",
    workingUnit: "",
    signingOfficial: "",
    signingOfficialTitle: "",
    mitraId: "",
    prospectName: "",
    prospectEmail: "",
    prospectPhone: "",
    offer_date: "",
    offer_period: "",
    validUntil: "",
    startDate: "",
    endDate: "",
    revenueType: "REGULAR",
  },
  infoErr: {},
  selectedService: "",
  selectedUnits: [],
  filterProperty: "",
  filterBuilding: "",
  filterFloor: "",
  periods: [{ label: "", periodStart: "", periodEnd: "", costs: [] }],
  revenueForm: {
    type: "REGULAR",
    shareBasis: "GROSS_SALES",
    revenueShareMethod: "",
    flatRate: { isActive: false, ratePercent: "" },
    mgp: { isActive: false, mgpAmount: "", mgpRatePercent: "" },
    yearlyRates: [],
    conditionalRate: { tiers: [] },
    tieredScheme: { tiers: [] },
    salesBracket: {
      lowRate: 7,
      threshold: 100000000,
      highRate: 10,
      maxRevShare: 10000000,
    },
    settlementSchedule: {
      frequency: "monthly",
      daily: { hour: "08" },
      weekly: { day: "1" },
      monthly: { date: "1" },
      bimonthly: { monthOffset: 1, date: "1" },
      quarterly: { monthOffset: 1, date: "1" },
      fourmonthly: { monthOffset: 1, date: "1" },
      semester: { monthOffset: 1, date: "1" },
      yearly: { date: "" },
      billingConfig: {
        billingOffsetDays: 0,
        dueOffsetDays: 14,
      },
    },
  },
};

const offeringSlice = createSlice({
  name: "offering",
  initialState,
  reducers: {
    clearOfferingState: () => initialState,
    setField: (state, action) => {
      state[action.payload.key] = action.payload.value;
    },
    resetForm: () => initialState,
  },
  extraReducers(builder) {
    const cases = [
      [getOfferingsThunk, "getOfferings"],
      [getOfferingByIdThunk, "getOfferingById"],
      [createOfferingThunk, "createOffering"],
      [reviseOfferingThunk, "reviseOffering"],
      [submitOfferingThunk, "submitOffering"],
      [saveOfferingThunk, "saveOffering"],
      [actApprovalThunk, "actApproval"],
      [sendOfferingThunk, "sendOffering"],
      [acceptOfferingThunk, "acceptOffering"],
      [declineOfferingThunk, "declineOffering"],
      [cancelOfferingThunk, "cancelOffering"],
      [convertOfferingThunk, "convertOffering"],
      [setOfferingRevenueSchemeThunk, "setRevenueScheme"],
    ];
    for (const [thunk, key] of cases) {
      builder
        .addCase(thunk.pending, (state) => {
          state[key] = pending(state[key]);
        })
        .addCase(thunk.fulfilled, (state, action) => {
          state[key] = fulfilled(state[key], action);
        })
        .addCase(thunk.rejected, (state, action) => {
          state[key] = rejected(state[key], action);
        });
    }
  },
});

export const offeringAction = {
  ...offeringSlice.actions,
  getOfferingsThunk,
  getOfferingByIdThunk,
  createOfferingThunk,
  reviseOfferingThunk,
  submitOfferingThunk,
  actApprovalThunk,
  sendOfferingThunk,
  acceptOfferingThunk,
  declineOfferingThunk,
  cancelOfferingThunk,
  convertOfferingThunk,
  saveOfferingThunk,
  setOfferingRevenueSchemeThunk,
};

export const selectOffering = (state) => state?.offering;

export default offeringSlice.reducer;
